import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { FormattedNote } from 'components/common';
import {
  useBtlApplicationInProgressById,
  useBtlNotes,
  useConveyancing,
  useGetAffordabilityCheck,
} from 'queries/btlApplications';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { notesApi } from 'api/notes';
import { useCanAccessHppPages } from 'hooks';
import {
  useIsOoCaseDipAcceptedEditEnabled,
  useIsOoCaseNewSectionsEnabled,
  useIsOoCaseSolicitorsEnabled,
  useIsOoCaseSubmissionEnabled,
} from 'providers/PosthogProvider';
import { isOoAffordabilityPassed } from './Affordability/ooAffordabilityHelpers';

export function useOoApplicationViewData(caseId: string) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: conveyancing, isLoading: isConveyancingLoading } = useConveyancing(caseId);
  const { data: caseData, isLoading: isCaseLoading } = useBtlApplicationInProgressById(caseId);
  const { data: affordabilityRuns } = useGetAffordabilityCheck(caseId);
  const { data: notes, isLoading: isNotesLoading } = useBtlNotes(caseId);
  const isNewSectionsEnabled = useIsOoCaseNewSectionsEnabled();
  const canAccessSolicitorsPage = useIsOoCaseSolicitorsEnabled();
  const canEditDipAcceptedSections = useIsOoCaseDipAcceptedEditEnabled();
  const canAccessSubmissionPages = useIsOoCaseSubmissionEnabled();
  const { canAccess: canAccessHppPages } = useCanAccessHppPages();
  const isAffordabilityPassed = isOoAffordabilityPassed(affordabilityRuns, caseData?.isAffordabilityRunRequired);

  const bankAccount = caseData?.borrower?.bankAccount;
  const valuation = caseData?.property?.valuation;
  const isCaseInitialLoading = isCaseLoading && !caseData;

  const sidebarContext = useMemo<OoCaseSidebarContext>(
    () => ({
      status: caseData?.status ?? '',
      isNewSectionsEnabled,
      canAccessHppPages,
      canAccessSolicitorsPage,
      canEditDipAcceptedSections,
      canAccessSubmissionPages,
      isAffordabilityPassed,
    }),
    [
      caseData?.status,
      isNewSectionsEnabled,
      canAccessHppPages,
      canAccessSolicitorsPage,
      canEditDipAcceptedSections,
      canAccessSubmissionPages,
      isAffordabilityPassed,
    ],
  );

  const formattedNotes: FormattedNote[] = useMemo(() => {
    if (!notes) {
      return [];
    }

    return notes.map(note => ({
      id: note.id,
      title: note.latestVersion.title,
      content: note.latestVersion.content,
      createdAt: note.createdAt,
      creator: note.latestVersion.creator
        ? `${note.latestVersion.creator.firstName} ${note.latestVersion.creator.lastName}`
        : '',
    }));
  }, [notes]);

  const { mutateAsync: createNote, isLoading: isCreatingNote } = useMutation(
    ({ title, content }: { title: string; content: string }) =>
      notesApi.createBtlNote(caseId, { title, content, isInternal: false }),
    {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: 'applicationDetails.note.create.success' }), { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['btl-notes', caseId] });
      },
      onError: (error: Error) => {
        enqueueSnackbar(error.message || 'Failed to create note', {
          variant: 'error',
        });
      },
    },
  );

  const handleCreateNote = useCallback(
    async (title: string, content: string) => {
      await createNote({ title, content });
    },
    [createNote],
  );

  return useMemo(
    () => ({
      caseId,
      case: caseData,
      valuation,
      isBankDetailsComplete: Boolean(bankAccount?.accountName),
      bankAccount,
      isCaseLoading: isCaseInitialLoading,
      isConveyancingLoading,
      conveyancing,
      sidebarContext,
      formattedNotes,
      onCreateNote: handleCreateNote,
      isCreatingNote: isNotesLoading || isCreatingNote,
    }),
    [
      bankAccount,
      caseData,
      caseId,
      conveyancing,
      formattedNotes,
      handleCreateNote,
      isCaseInitialLoading,
      isConveyancingLoading,
      isCreatingNote,
      isNotesLoading,
      sidebarContext,
      valuation,
    ],
  );
}
